import * as React from 'react';
import * as moment from 'moment';
import groupBy from 'lodash/groupBy';
import { BASE_DATE_FORMAT } from '@seeneco-ui/core/constants';
import { getPropInSafe, declOfNum } from '@seeneco-ui/core/utils';
import { prettyDate } from '@seeneco-ui/core/utils/date';
import Icon from '@seeneco-ui/core/components/icon';
import Button from '@seeneco-ui/core/components/button';
import IconLink from '@seeneco-ui/core/components/icon-link';
import Card from '@seeneco-ui/core/components/card';
import Box, { BoxProps } from '@seeneco-ui/core/components/box';
import FoldingItem from '@seeneco-ui/core/components/folding-item';
import Typography from '@seeneco-ui/core/components/typography';
import AccountIcon from '@seeneco-ui/core/components/account-icon';

import { integrationHelpLinks } from '../../../../shared/constants/cms';
import {
	Root,
	AccountName,
	BankIconLayout,
	GroupName,
	AddButtonLayout,
	Value,
} from './styled';


const Label: React.SFC<Partial<BoxProps>> = (props) => 
	<Typography.Label
		{...props}
		component='span'
		fontSize={12}>
		{props.children}
	</Typography.Label>;

export type ConnectedServicesListProps = {
	isFetching: boolean;
	accountList: Array<ExternalSystemAccount>;
	cmsMap: Record<string, CashManagementSystem>;
	ssiMap: Record<string, SubsystemInstance>;
	resMap: Record<string, RemoteExternalService>;
	selectedAccountID?: number;
	onClickAccount?: (ID: number) => (e) => void;
	onAdd: (cmsGUID: string) => (e) => void;
	onChange: (account: ExternalSystemAccount) => (e) => void;
	onRemove: (account: ExternalSystemAccount) => (e) => void;
};

const ConnectedServicesList: React.SFC<ConnectedServicesListProps> = (props) => {
	const {
		isFetching,
		accountList,
		cmsMap,
		ssiMap,
		resMap,
		selectedAccountID,
		onClickAccount,
		onAdd,
		onChange,
		onRemove,
	} = props;

	if (isFetching || accountList.length === 0 ) return null;
	const grouppedAccounts = groupBy(accountList, e => e.ExternalSSInstanceID);
	const now = moment();

	return (
			<Root>
				{
					Object.keys(grouppedAccounts).reverse().map((key, idx) => {
						const accountList = grouppedAccounts[key];
						const groupName = accountList[0].ExternalSSInstanceName;
						const cmsGUID = getPropInSafe(ssiMap, o => o[accountList[0].ExternalSSInstanceID].GUID, '');
						const cmsName = getPropInSafe(cmsMap, o => o[cmsGUID].Name, '');

						return (
							<FoldingItem
								key={idx}
								renderHeader={() => {
									return (
										<Box width='100%' display='flex' justifyContent='space-between'>
											<Box display='flex'>
												<BankIconLayout>
													<AccountIcon
														isBank
														bankName={cmsName}
														width={36}
														height={36}
													/>
												</BankIconLayout>
												<Box>
													<GroupName>
														{groupName}
													</GroupName>
													<Label>
														{`${accountList.length} ${declOfNum(accountList.length, ['уч. запись', 'уч. записи', 'уч. записей'])}`}
													</Label>
												</Box>
											</Box>
											<Box display='flex'>
												<AddButtonLayout>
													<Button
														appearance='text'
														size='small'
														stopPropagation
														onClick={onAdd(cmsGUID)}>
														<Icon type='add' color='primary' width={18} height={18} />
														Добавить еще одну учетную запись
													</Button>
												</AddButtonLayout>
												<IconLink
													to={integrationHelpLinks[cmsGUID]}
													rel='noopener'
													target='_blank'
													rightIconType='link-external'
													iconSize={7}
													iconColor='muted'
													color='muted'>
													Как подключить?
												</IconLink>
											</Box>
										</Box>
									)
								}}>
								{
									accountList.map((account, idx) => {
										const res = getPropInSafe(resMap, o => o[account.ExternalSSInstanceID], {});
										const isActive = selectedAccountID === account.ID;
										const isIdentityProviderRole = res.Roles && res.Roles.some(el => el.CLASSIFIER === 'IDENTITY_PROVIDER_ROLE');
										const isDataProviderRole = res.Roles && res.Roles.some(el => el.CLASSIFIER === 'DATA_PROVIDER_ROLE');
										const isOverdue = moment(account.ValidTo, BASE_DATE_FORMAT).isBefore(now, 'day');
										const status = !isOverdue
											? account.Validated
													? 'Действующая'
													:	'Неавторизованная'
											: 'Просроченная';

										return (
											<Card
												key={idx}
												appearance='repeater-item'
												width='100%'
												isActive={isActive}
												style={{ cursor: 'pointer' }}
												onClick={onClickAccount(account.ID)}>
												<Box width='100%' display='flex' justifyContent='space-between' alignItems='center'>
													<Box width='33.3333%' paddingRight='10px'>
														<Box>
															<Box marginBottom='5px'>
																{
																	account.Name
																	? <AccountName crossout={isOverdue}>{account.Name}</AccountName>
																	: <AccountName muted>Имя не указано</AccountName>
																}
															</Box>
															<Box>
																<Label>
																	{isIdentityProviderRole ? 'Удостоверяющий центр при аутентификации по SSO ' : ''}
																	{isDataProviderRole ? 'Провайдер данных' : ''}
																</Label>
															</Box>
														</Box>
													</Box>
													<Box
														width='33.3333%'
														display='flex'
														justifyContent='flex-end'
														alignItems='center'
														paddingRight='10px'>
														<Box>
															<Label marginRight='10px'>Статус учетной записи:</Label>
															<Value red={isOverdue}>{status}</Value>
														</Box>
													</Box>
													<Box
														width='33.3333%'
														display='flex'
														justifyContent='flex-end'
														alignItems='center'>
														<Label marginRight='10px'>Срок действия:</Label>
														<Value>{prettyDate(account.ValidTo)}</Value>
													</Box>
												</Box>
												{
													isActive &&
													<Box width='100%' display='flex' justifyContent='flex-end'>
														<Box marginRight='10px'>
															<Button
																appearance='text'
																size='small'
																stopPropagation
																onClick={onChange(account)}>
																<Icon type='edit' color='primary' width={16} height={16} />
																{'\u00A0'}
																Изменить
															</Button>
														</Box>
														<Box>
															<Button
																appearance='text'
																size='small'
																color='default'
																stopPropagation
																onClick={onRemove(account)}>
																<Icon type='clear' color='muted' width={16} height={16} />
																{'\u00A0'}
																Удалить
															</Button>
														</Box>
													</Box>
												}
											</Card>
										) 
									})
								}
							</FoldingItem>
						)
					})
				}
			</Root>
	);
}

export default ConnectedServicesList;
